import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { StatusGlyph, STATUS_META } from '../components/StatusGlyph';
import type { EvaluatedObjective, ObjectiveStatus } from '../lib/api';
import { useSession } from '../lib/session';
import '../styles/objectives.css';

const TONE_ORDER = { active: 0, gated: 1, broken: 2, dormant: 3 } as const;

const STATUS_SORT = (a: EvaluatedObjective, b: EvaluatedObjective): number =>
  TONE_ORDER[STATUS_META[a.status].tone] - TONE_ORDER[STATUS_META[b.status].tone] ||
  a.objective.title.localeCompare(b.objective.title, 'es');

type FilterKey = 'all' | 'active' | 'gated' | 'dormant';

function actionRoute(kind: string, route?: string): string | null {
  switch (kind) {
    case 'upgrade':
    case 'billing':
      return '/administracion/suscripcion';
    case 'license':
      return '/cumplimiento/licencias';
    case 'setup':
      return route ?? '/administracion/usuarios';
    default:
      return null; // 'role' se resuelve pidiéndolo a un administrador
  }
}

function ObjectiveRow({ o }: { o: EvaluatedObjective }) {
  const nav = useNavigate();
  const meta = STATUS_META[o.status];
  const toneClass = `tone-${meta.tone}`;
  const rowClass =
    'obj' +
    (meta.tone === 'gated' ? ' obj--gated' : '') +
    (meta.tone === 'dormant' ? ' obj--dormant' : '') +
    (meta.tone === 'broken' ? ' obj--broken' : '');

  const dest = o.action ? actionRoute(o.action.kind, o.objective.route) : null;

  return (
    <div className={rowClass}>
      <span className={`obj__mark ${toneClass}`}>
        <StatusGlyph status={o.status} />
      </span>

      <div className="obj__body">
        <div className="obj__title">{o.objective.title}</div>
        <div className="obj__detail">{o.objective.detail}</div>
      </div>

      <div className="obj__right">
        {o.status === 'active' && o.metric ? (
          <span className={`obj__metric${o.metric.onTarget ? ' obj__metric--on' : ''}`}>
            {o.metric.label}: <b>{o.metric.value}</b>
            {o.metric.target != null && <span className="faint">/ {o.metric.target}</span>}
          </span>
        ) : o.status === 'active' ? (
          <span className={`obj__status ${toneClass}`}>{meta.label}</span>
        ) : (
          <>
            <span className="obj__reason">{o.reason}</span>
            {o.action && dest ? (
              <button className="btn btn--unlock" onClick={() => nav(dest)}>
                {o.action.label}
                <Icon name="arrow-up-right" size={13} />
              </button>
            ) : (
              <span className={`obj__status ${toneClass}`}>{meta.label}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function Objectives() {
  const { caps, company, loadingCaps } = useSession();
  const [filter, setFilter] = useState<FilterKey>('all');

  const grouped = useMemo(() => {
    if (!caps) return [];
    const byModule = new Map<string, EvaluatedObjective[]>();
    for (const o of caps.objectives) {
      const arr = byModule.get(o.objective.module) ?? [];
      arr.push(o);
      byModule.set(o.objective.module, arr);
    }
    return caps.modules
      .map((m) => ({
        module: m.module,
        label: m.label,
        active: m.active,
        total: m.total,
        objectives: (byModule.get(m.module) ?? []).slice().sort(STATUS_SORT),
      }))
      .filter((g) => g.objectives.length > 0);
  }, [caps]);

  const strip = useMemo(() => {
    if (!caps) return [];
    return caps.objectives
      .slice()
      .sort(
        (a, b) =>
          (caps.modules.findIndex((m) => m.module === a.objective.module) -
            caps.modules.findIndex((m) => m.module === b.objective.module)) ||
          STATUS_SORT(a, b),
      )
      .map((o) => STATUS_META[o.status].tone);
  }, [caps]);

  if (!caps || loadingCaps) {
    return (
      <main className="main">
        <div className="faint" style={{ padding: 40 }}>Loading objectives…</div>
      </main>
    );
  }

  const s = caps.summary;
  const active = s.active ?? 0;
  const gated = (s.locked_plan ?? 0) + (s.needs_setup ?? 0) + (s.no_permission ?? 0) + (s.license_missing ?? 0);
  const broken = (s.license_expired ?? 0) + (s.plan_suspended ?? 0);
  const dormant = s.not_applicable ?? 0;
  const total = caps.objectives.length;

  const passesFilter = (status: ObjectiveStatus): boolean => {
    const t = STATUS_META[status].tone;
    if (filter === 'all') return true;
    if (filter === 'active') return t === 'active';
    if (filter === 'dormant') return t === 'dormant';
    return t === 'gated' || t === 'broken'; // 'gated'
  };

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Objectives</span>
            <h1 className="h1">What {company?.tradeName} can do</h1>
            <p className="page-head__sub">
              La plataforma se ajusta a tu licencia comercial y a tu plan. Cada objetivo muestra su
              status and, if locked, exactly what is needed to unlock it.
            </p>
          </div>
        </div>
      </div>

      {/* Superficie operativa — la firma */}
      <section className="surface">
        <div className="surface__top">
          <div className="surface__count">
            <span className="surface__big">{active}</span>
            <span className="surface__of">/ {total} objetivos activos</span>
          </div>
          <div className="legend">
            <span className="legend__item">
              <span className="legend__swatch legend__swatch--active" /> Activo <span className="legend__n">{active}</span>
            </span>
            <span className="legend__item">
              <span className="legend__swatch legend__swatch--gated" /> Cerrado <span className="legend__n">{gated}</span>
            </span>
            {broken > 0 && (
              <span className="legend__item">
                <span className="legend__swatch legend__swatch--broken" /> Roto <span className="legend__n">{broken}</span>
              </span>
            )}
            <span className="legend__item">
              <span className="legend__swatch legend__swatch--dormant" /> No aplica <span className="legend__n">{dormant}</span>
            </span>
          </div>
        </div>

        <div className="strip" role="img" aria-label={`${active} de ${total} objetivos activos`}>
          {strip.map((t, i) => (
            <span
              key={i}
              className={`seg seg--${t}`}
              style={{ animationDelay: `${Math.min(i * 12, 600)}ms` }}
            />
          ))}
        </div>

        <div className="filterbar">
          {([
            ['all', 'Todos', total],
            ['active', 'Activos', active],
            ['gated', 'Cerrados', gated + broken],
            ['dormant', 'No aplican', dormant],
          ] as [FilterKey, string, number][]).map(([key, label, n]) => (
            <button
              key={key}
              className={`chip-toggle${filter === key ? ' chip-toggle--on' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label}
              <span className="chip-toggle__n">{n}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Grupos por módulo */}
      {grouped.map((g) => {
        const rows = g.objectives.filter((o) => passesFilter(o.status));
        if (!rows.length) return null;
        return (
          <section className="modgroup" key={g.module}>
            <div className="modgroup__head">
              <span className="modgroup__name">{g.label}</span>
              <span className="modgroup__rule" />
              <span className="modgroup__ratio">
                <span className={g.active > 0 ? 'on' : ''}>{g.active}</span> / {g.total} activos
              </span>
            </div>
            {rows.map((o) => (
              <ObjectiveRow key={o.objective.id} o={o} />
            ))}
          </section>
        );
      })}
    </main>
  );
}
